import { defineComponent as _defineComponent } from 'vue'
import { ImageTransform } from "../types";
import { onMounted, ref, watch } from "vue";
import { clamp } from "../lib/clamp";
import { useCanvases } from "../lib/canvases";


export default /*#__PURE__*/_defineComponent({
  __name: 'ResizeImageCanvas',
  props: {
    image: null,
    transform: null,
    height: null,
    width: null,
    fill: { type: Boolean },
    name: null
  },
  setup(__props: any) {

const props = __props as {
  image?: HTMLImageElement;
  transform: ImageTransform;
  height?: number;
  width?: number;
  fill?: boolean;
  name?: string;
}

const { registerCanvas } = useCanvases();

const canvasElement = ref<HTMLCanvasElement>();



const draw = async (): Promise<void> => {
  const { image } = props;

  if (!props.image || !canvasElement.value) {
    return;
  }

  const canvas = canvasElement.value;
  const ctx = canvasElement.value?.getContext("2d");

  if (!ctx) {
    return;
  }

  const naturalWidth = props.image.naturalWidth;
  const naturalHeight = props.image.naturalHeight;

  if (!props.width || !props.height) {
    Object.assign(canvas, {
      width: naturalWidth,
      height: naturalHeight
    });
  }

  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;

  const scale = Math.max(canvasWidth / naturalWidth, canvasHeight / naturalHeight);

  const scaledWidth = Math.floor(naturalWidth * scale);
  const scaledHeight = Math.floor(naturalHeight * scale);

  // ctx.drawImage dx and dy respresent where to start drawing the image. When the image needs to be cropped to fit, this is going to be a negative value. These intially values give us the draw position offsets to crop to the exact horizontal and vertical center.
  const dx = Math.round(canvasWidth / 2 - (naturalWidth / 2) * scale);
  const dy = Math.round(canvasHeight / 2 - (naturalHeight / 2) * scale);

  // Calculate how to shift the dx and dy to centre the focus point as best as possible for the given aspect ratio
  // fx and fy represent how would transform dx and dy in order to exactly center the focus point
  const { focusX, focusY } = props.transform;

  const fx = Math.round(scaledWidth / 2 - scaledWidth * focusX);
  const fy = Math.round(scaledHeight / 2 - scaledHeight * focusY);

  // Focused dx and dy values fdx and fdy
  // It's not always possible to exactly centre a focus point as extreme focus values would draw most of the image off the canvas and leave empty space in the canvas with no image.
  const fdx = clamp(dx * 2, dx + fx, 0);
  const fdy = clamp(dy * 2, dy + fy, 0);

  // Draw the image on the canvas
  requestAnimationFrame(() => {
    // Clear the canvas between draws to clear previously drawn image
    // This becomes apparent when drawing transparent images
    clearCanvas(ctx);

    if (image) {
      ctx.drawImage(image, fdx, fdy, scaledWidth, scaledHeight);
    }
  });
};

const clearCanvas = (ctx: CanvasRenderingContext2D) => {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
};

const clear = (): void => {
  const ctx = canvasElement.value?.getContext("2d");

  if (ctx) {
    clearCanvas(ctx);
  }
};

watch(
  () => [props.image, props.transform],
  ([newImage]) => {
    if (newImage) {
      draw();
    } else {
      clear();
    }
  }
);

onMounted(() => {
  if (props.name && canvasElement.value) {
    registerCanvas(props.name, canvasElement.value);
  }

  draw();
});

return { __sfc: true,registerCanvas, canvasElement, props, draw, clearCanvas, clear }
}

})